import { AlertCircle, ChevronLeft, Lock, X } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { usePlanStatus } from '../../hooks/usePlanStatus';
import { isSamePack } from '../../utils/planUpgrade';
import { GGJLogoFull } from '../Brand/GGJLogo';
import { BRAND_LOGO_DISPLAY } from '../../config/brandLogos';
import LoadingSpinner from '../LoadingSpinner';
import CheckoutPlanSwitcher from './CheckoutPlanSwitcher';
import type { useCreditsCheckout } from './useCreditsCheckout';
import { localizedPackName, localizedPackPeriodDisplay, localizedPackPeriodShort } from './creditCheckoutUtils';
import InternationalPhoneInput from '../Shared/InternationalPhoneInput';
import CountryAutocomplete from '../Shared/CountryAutocomplete';
import { isValidPhoneForCountry } from '../../utils/phoneCountries';

type CheckoutState = ReturnType<typeof useCreditsCheckout>;

type CreditCheckoutViewProps = CheckoutState & {
  onBack: () => void;
};

const CreditCheckoutView: React.FC<CreditCheckoutViewProps> = ({
  step,
  selectedPlan,
  paymentFailure,
  dismissPaymentFailure,
  billingOpen,
  setBillingOpen,
  userData,
  updateBilling,
  updateCountry,
  updatePhoneCountry,
  updatePhoneNational,
  getLocaleGstRate,
  formatPlanDisplayPrice,
  formatSwitcherPlanPrice,
  formatCheckoutTotal,
  formatLocaleSubtotal,
  formatLocaleGst,
  formatZeroTax,
  handlePayAndUpgrade,
  selectPlan,
  enableAutoRenew,
  setEnableAutoRenew,
  useSubscriptionCheckout,
  onBack,
}) => {
  const { t } = useTranslation();
  const planStatus = usePlanStatus();

  if (!selectedPlan) return null;

  const planName = localizedPackName(selectedPlan.id, t);
  const planPeriodShort = localizedPackPeriodShort(selectedPlan.id, t);
  const planPeriodDisplay = localizedPackPeriodDisplay(selectedPlan.id, t);

  const isCurrentPlanSelected =
    planStatus.isActive
    && !planStatus.isExpired
    && isSamePack(selectedPlan.id, planStatus.planId);

  const showProcessingOverlay = step === 'processing';
  const hasPaymentFailure = Boolean(paymentFailure);
  const phoneMissing = useSubscriptionCheckout && (
    userData.phoneCountry !== 'IN' || !isValidPhoneForCountry('IN', userData.phone)
  );
  const nameMissing = useSubscriptionCheckout && (!userData.firstName.trim() || !userData.lastName.trim());
  const oneTimePhoneMissing = !useSubscriptionCheckout && selectedPlan.autoRenew && (
    userData.phoneCountry !== 'IN' || !isValidPhoneForCountry('IN', userData.phone)
  );

  const gstRatePct = getLocaleGstRate(selectedPlan);

  return (
    <>
      <aside className="credit-checkout-summary">
        <div className="credit-checkout-summary-inner">
          <div className="credit-checkout-topbar">
            <button type="button" onClick={onBack} className="credit-checkout-back" aria-label={t('creditsPurchaseModal.backToPlans')}>
              <ChevronLeft className="w-4 h-4" strokeWidth={2} />
            </button>
            <GGJLogoFull
              iconSize={BRAND_LOGO_DISPLAY.checkoutIcon}
              fontSize={BRAND_LOGO_DISPLAY.checkoutFont}
              className="credit-checkout-logo"
            />
          </div>
          <p className="credit-checkout-headline">
            {useSubscriptionCheckout
              ? t('creditsPurchaseModal.recurringHeadline', { plan: planName })
              : t('creditsPurchaseModal.oneTimeHeadline', { plan: planName })}
          </p>
          <p className="credit-checkout-price">
            {formatCheckoutTotal(selectedPlan)}
            {planPeriodDisplay && (
              <span className="credit-checkout-price-period"> / {planPeriodDisplay}</span>
            )}
          </p>
          {gstRatePct > 0 && (
            <p className="credit-checkout-price-meta">
              {t('creditsPurchaseModal.priceInclGst', {
                defaultValue: 'Includes {{rate}}% GST · base {{base}}',
                rate: gstRatePct,
                base: formatPlanDisplayPrice(selectedPlan),
              })}
            </p>
          )}
          <CheckoutPlanSwitcher
            selectedPackId={selectedPlan.id}
            onSelectPlan={selectPlan}
            formatSwitcherPlanPrice={formatSwitcherPlanPrice}
          />
          <hr className="credit-checkout-divider" />
          <div className="credit-checkout-line-item">
            <div>
              <strong>
                {t('creditsPurchaseModal.productLineTitle', {
                  defaultValue: 'GoGrabJob {{plan}}',
                  plan: planName,
                })}
              </strong>
              <span>
                {useSubscriptionCheckout
                  ? t('creditsPurchaseModal.recurringSubscriptionDetail', {
                      defaultValue: 'Recurring subscription · {{period}}',
                      period: planPeriodShort,
                    })
                  : selectedPlan.autoRenew
                    ? t('creditsPurchaseModal.oneTimePaymentDetail', {
                        defaultValue: 'One-time payment · {{period}}',
                        period: planPeriodShort,
                      })
                    : t('creditsPurchaseModal.oneTimeCharge', 'One-time charge')}
              </span>
            </div>
            <div>{formatPlanDisplayPrice(selectedPlan)}</div>
          </div>
          <div className="credit-checkout-totals">
            <div className="credit-checkout-totals-row">
              <span>{t('creditsPurchaseModal.subtotalLabel', 'Subtotal')}</span>
              <span>{formatLocaleSubtotal(selectedPlan)}</span>
            </div>
            {gstRatePct > 0 ? (
              <div className="credit-checkout-totals-row">
                <span>{t('creditsPurchaseModal.taxLabel', { rate: gstRatePct, defaultValue: 'GST ({{rate}}%)' })}</span>
                <span>+ {formatLocaleGst(selectedPlan)}</span>
              </div>
            ) : (
              <div className="credit-checkout-totals-row">
                <span>{t('creditsPurchaseModal.taxExportLabel', 'Tax (export)')}</span>
                <span>{formatZeroTax()}</span>
              </div>
            )}
            <div className="credit-checkout-total-due">
              <span>{t('creditsPurchaseModal.totalDueToday', 'Total due today')}</span>
              <span>{formatCheckoutTotal(selectedPlan)}</span>
            </div>
          </div>
        </div>
      </aside>
      <main className="credit-checkout-main">
        <div className="credit-checkout-main-inner">
          {hasPaymentFailure && (
            <div className="credit-checkout-failure-banner" role="alert">
              <div className="credit-checkout-failure-banner-icon" aria-hidden>
                <AlertCircle className="w-5 h-5" />
              </div>
              <div className="credit-checkout-failure-banner-copy">
                <p className="credit-checkout-failure-banner-title">
                  {t('creditsPurchaseModal.paymentFailureBannerTitle')}
                </p>
                <p className="credit-checkout-failure-banner-message">{paymentFailure}</p>
              </div>
              <button
                type="button"
                className="credit-checkout-failure-banner-dismiss"
                onClick={dismissPaymentFailure}
                aria-label={t('creditsPurchaseModal.cancel')}
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
          <div className="credit-checkout-form">
            <h2 className="credit-checkout-section-title">{t('creditsPurchaseModal.contactInformation')}</h2>
            <div className="credit-checkout-field">
              <label htmlFor="checkout-email">{t('creditsPurchaseModal.email', 'Email')}</label>
              <input
                id="checkout-email"
                type="email"
                value={userData.email}
                onChange={(e) => updateBilling('email', e.target.value)}
                placeholder={t('creditsPurchaseModal.emailPlaceholder', 'you@example.com')}
                autoComplete="email"
                disabled={showProcessingOverlay}
                className={`credit-checkout-input${userData.email ? ' credit-checkout-input--prefilled' : ''}`}
              />
            </div>
            <InternationalPhoneInput
              id="checkout-phone"
              countryIso2={userData.phoneCountry}
              nationalDigits={userData.phone}
              onCountryChange={updatePhoneCountry}
              onNationalChange={updatePhoneNational}
              disabled={showProcessingOverlay}
              invalid={phoneMissing || oneTimePhoneMissing}
              prefilled={Boolean(userData.phone)}
              label={(
                <>
                  {t('creditsPurchaseModal.mobileNumber', { defaultValue: 'Mobile number' })}
                  {(useSubscriptionCheckout || selectedPlan.autoRenew) ? ' *' : ''}
                </>
              )}
              hint={useSubscriptionCheckout
                ? t('creditsPurchaseModal.mobileNumberHint', {
                  defaultValue: 'Required for UPI AutoPay and payment receipts. Saved to your profile.',
                })
                : t('creditsPurchaseModal.mobileNumberOneTimeHint', {
                  defaultValue: 'Required for payment receipts. Saved to your profile.',
                })}
            />
            {useSubscriptionCheckout && (
              <div className="credit-checkout-billing-grid credit-checkout-billing-grid--contact">
                <div>
                  <label htmlFor="checkout-contact-first-name">
                    {t('creditsPurchaseModal.firstName')} *
                  </label>
                  <input
                    id="checkout-contact-first-name"
                    type="text"
                    autoComplete="given-name"
                    value={userData.firstName}
                    onChange={(e) => updateBilling('firstName', e.target.value)}
                    disabled={showProcessingOverlay}
                    className={`credit-checkout-input touch-manipulation${
                      userData.firstName ? ' credit-checkout-input--prefilled' : ''
                    }${nameMissing && !userData.firstName.trim() ? ' credit-checkout-input--missing' : ''}`}
                  />
                </div>
                <div>
                  <label htmlFor="checkout-contact-last-name">
                    {t('creditsPurchaseModal.lastName')} *
                  </label>
                  <input
                    id="checkout-contact-last-name"
                    type="text"
                    autoComplete="family-name"
                    value={userData.lastName}
                    onChange={(e) => updateBilling('lastName', e.target.value)}
                    disabled={showProcessingOverlay}
                    className={`credit-checkout-input touch-manipulation${
                      userData.lastName ? ' credit-checkout-input--prefilled' : ''
                    }${nameMissing && !userData.lastName.trim() ? ' credit-checkout-input--missing' : ''}`}
                  />
                </div>
              </div>
            )}
            <h2 className="credit-checkout-section-title">{t('creditsPurchaseModal.paymentMethod')}</h2>
            <div className="credit-checkout-payment-panel" role="region" aria-labelledby="checkout-payment-heading">
              <div className="credit-checkout-payment-panel-icon" aria-hidden>
                <Lock className="w-5 h-5" strokeWidth={2} />
              </div>
              <div>
                <p id="checkout-payment-heading" className="credit-checkout-payment-panel-title">
                  {useSubscriptionCheckout
                    ? t('creditsPurchaseModal.recurringPaymentTitle')
                    : t('creditsPurchaseModal.checkoutPaymentTitle')}
                </p>
                <p className="credit-checkout-payment-panel-copy">
                  {useSubscriptionCheckout
                    ? t('creditsPurchaseModal.recurringPaymentDescription', {
                        period: planPeriodShort || t('views.pricing.perMonth', 'per month'),
                      })
                    : t('creditsPurchaseModal.checkoutPaymentDescription')}
                </p>
                <p className="credit-checkout-cashfree-hint">{t('creditsPurchaseModal.cashfreeMethods')}</p>
              </div>
            </div>
            <CountryAutocomplete
              id="checkout-country-select"
              label={t('creditsPurchaseModal.countryOrRegion')}
              value={userData.country}
              onChange={updateCountry}
              disabled={showProcessingOverlay}
              prefilled={Boolean(userData.country)}
              placeholder={t('creditsPurchaseModal.searchCountry', { defaultValue: 'Search country or region…' })}
            />
            {billingOpen && (
              <div className="credit-checkout-billing-extra">
                <p className="credit-checkout-billing-note">{t('creditsPurchaseModal.prefilledFromProfile')}</p>
                {!useSubscriptionCheckout && (
                  <div className="credit-checkout-billing-grid">
                    <div>
                      <label htmlFor="checkout-first-name">{t('creditsPurchaseModal.firstName')}</label>
                      <input id="checkout-first-name" type="text" value={userData.firstName} onChange={(e) => updateBilling('firstName', e.target.value)} className="credit-checkout-input" />
                    </div>
                    <div>
                      <label htmlFor="checkout-last-name">{t('creditsPurchaseModal.lastName')}</label>
                      <input id="checkout-last-name" type="text" value={userData.lastName} onChange={(e) => updateBilling('lastName', e.target.value)} className="credit-checkout-input" />
                    </div>
                  </div>
                )}
                <div className="credit-checkout-field">
                  <label htmlFor="checkout-address">{t('creditsPurchaseModal.billingAddress')}</label>
                  <input id="checkout-address" type="text" value={userData.billingAddress} onChange={(e) => updateBilling('billingAddress', e.target.value)} className="credit-checkout-input" />
                </div>
              </div>
            )}
            <button type="button" onClick={() => setBillingOpen((o) => !o)} className="credit-checkout-billing-link">
              {billingOpen ? t('creditsPurchaseModal.hideBillingDetails') : t('creditsPurchaseModal.billingDetails')}
            </button>
          </div>
          <div className="credit-checkout-footer">
            <button
              type="button"
              onClick={handlePayAndUpgrade}
              disabled={!userData.email.trim() || showProcessingOverlay || isCurrentPlanSelected}
              className={`credit-checkout-subscribe${
                !userData.email.trim() || showProcessingOverlay || isCurrentPlanSelected
                  ? ' credit-checkout-subscribe--disabled'
                  : ''
              }`}
            >
              {showProcessingOverlay ? (
                <LoadingSpinner size="sm" text="" />
              ) : (
                <span>
                  {isCurrentPlanSelected
                    ? t('creditsPurchaseModal.currentPlanActive', { defaultValue: 'Current plan active' })
                    : hasPaymentFailure
                      ? t('creditsPurchaseModal.tryAgain')
                      : useSubscriptionCheckout
                        ? t('creditsPurchaseModal.authorizeRecurring', { amount: formatCheckoutTotal(selectedPlan) })
                        : t('creditsPurchaseModal.continueToPayment', { amount: formatCheckoutTotal(selectedPlan) })}
                </span>
              )}
            </button>
            {selectedPlan.autoRenew && (
              <label className="credit-checkout-auto-renew">
                <input
                  type="checkbox"
                  className="credit-checkout-auto-renew-input"
                  checked={enableAutoRenew}
                  onChange={(e) => setEnableAutoRenew(e.target.checked)}
                  disabled={showProcessingOverlay || isCurrentPlanSelected}
                />
                <span className="credit-checkout-auto-renew-copy">
                  <span className="credit-checkout-auto-renew-label">
                    {t('creditsPurchaseModal.autoRenewCheckboxLabel', {
                      defaultValue: 'Enable auto-renew (UPI AutoPay or card mandate)',
                    })}
                  </span>
                  <span className="credit-checkout-auto-renew-hint">
                    {t('creditsPurchaseModal.autoRenewCheckboxHint', {
                      defaultValue: 'Uncheck for a one-time payment only — no recurring billing. Credits for this period are granted once.',
                    })}
                  </span>
                </span>
              </label>
            )}
            {isCurrentPlanSelected && (
              <p className="credit-checkout-cta-hint">
                {t('creditsPurchaseModal.currentPlanManageHint', {
                  defaultValue: 'Select a higher plan above to upgrade, or manage billing under Account → Plan.',
                })}
              </p>
            )}
            {!isCurrentPlanSelected && useSubscriptionCheckout && (
              <>
                <p className="credit-checkout-cta-hint">
                  {t('creditsPurchaseModal.recurringSessionExpiryHint', {
                    minutes: 30,
                    defaultValue: 'Complete mandate authorization within 30 minutes — the Cashfree link expires after that.',
                  })}
                </p>
                <p className="credit-checkout-cta-hint" style={{ fontSize: '11px', lineHeight: '1.5', opacity: 0.8 }}>
                  {t('creditsPurchaseModal.mandateCancelAnytime', {
                    defaultValue: '🔒 Standard auto-debit setup. Cancel anytime in 1-click from your dashboard — no lock-in period.',
                  })}
                </p>
                <p className="credit-checkout-cta-hint" style={{ fontSize: '11px', lineHeight: '1.5', opacity: 0.8 }}>
                  {t('creditsPurchaseModal.abandonHint', {
                    defaultValue: '↩ Changed your mind on the payment screen? Press your browser\'s back button to return here safely.',
                  })}
                </p>
              </>
            )}
            {!isCurrentPlanSelected && (
              <p className="credit-checkout-cta-hint">
                {useSubscriptionCheckout
                  ? t('creditsPurchaseModal.recurringCtaHint')
                  : t('creditsPurchaseModal.checkoutCtaHint')}
              </p>
            )}
            <p className="credit-checkout-legal">
              {useSubscriptionCheckout
                ? t('creditsPurchaseModal.recurringLegal')
                : t('creditsPurchaseModal.oneTimeLegal')}
            </p>
          </div>
        </div>
      </main>

      {showProcessingOverlay && (
        <div className="credit-checkout-processing-overlay" role="dialog" aria-modal aria-labelledby="checkout-processing-title">
          <div className="credit-checkout-processing-modal">
            <LoadingSpinner size="md" text="" />
            <h2 id="checkout-processing-title">{t('creditsPurchaseModal.processingPayment')}</h2>
            <p>{t('creditsPurchaseModal.redirectingPleaseWait')}</p>
          </div>
        </div>
      )}

    </>
  );
};

export default CreditCheckoutView;
